import { Component, computed, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { BadgeColour, ComponentsModule } from '@mastechnicaegroup/carelinelive-ui';
import { ActivityService } from '@lib/activity/activity.service';
import { Activity } from '@lib/api/models/activity';
import { PaginatedApiResponse, Meta } from '@lib/api/responses/paginated-api-response';
import { AppDatePipe } from '@shared/pipes/date.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, switchMap, map } from 'rxjs';

interface ChangedField {
    key: string;
    label: string;
    newValue: string;
}

@Component({
    selector: 'app-region-audit-tab',
    imports: [
        CommonModule,
        ComponentsModule,
        AppDatePipe,
    ],
    templateUrl: './region-audit-tab.component.html',
    standalone: true,
    styleUrl: './region-audit-tab.component.scss'
})
export class RegionAuditTabComponent implements OnInit {

    private readonly _activityService = inject(ActivityService);
    private readonly _route = inject(ActivatedRoute);
    private readonly _router = inject(Router);
    private readonly _destroyRef = inject(DestroyRef);

    @Input() externalRefresh$: Observable<void> | null = null;

    private readonly _page$ = new BehaviorSubject<number>(
        Number(this._route.snapshot.queryParams['auditPage'] ?? 1)
    );

    protected readonly activities = signal<Activity[]>([]);
    protected readonly meta = signal<Meta | null>(null);
    protected readonly isLoading = signal(true);
    protected readonly error = signal<string | null>(null);

    protected readonly currentPage = computed(() => this.meta()?.current_page ?? 1);
    protected readonly lastPage = computed(() => this.meta()?.last_page ?? 1);
    protected readonly hasPrev = computed(() => this.currentPage() > 1);
    protected readonly hasNext = computed(() => this.currentPage() < this.lastPage());

    constructor() {
        this._page$.pipe(
            switchMap(page =>
                this._route.params.pipe(
                    map(params => params['id'] as string),
                    switchMap(regionId =>
                        this._activityService.getActivity('region', regionId, page)
                    )
                )
            ),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe({
            next: (response: PaginatedApiResponse<Activity>) => {
                this.activities.set(response.data);
                this.meta.set(response.meta);
                this.isLoading.set(false);
            },
            error: () => {
                this.error.set('Failed to load audit log.');
                this.isLoading.set(false);
            }
        });
    }

    ngOnInit(): void {
        if (this.externalRefresh$) {
            this.externalRefresh$.pipe(
                takeUntilDestroyed(this._destroyRef)
            ).subscribe(() => this.refresh());
        }
    }

    protected eventBadgeColour(event: string | null): BadgeColour {
        switch (event) {
            case 'created':
            case 'priority_added':
            case 'zone_added':
                return 'success';
            case 'updated':
            case 'priority_updated':
            case 'zone_updated':
                return 'primary';
            case 'deleted':
            case 'provider_removed':
                return 'error';
            default:            return 'variant';
        }
    }

    protected eventLabel(event: string | null): string {
        switch (event) {
            case 'created':           return 'CREATED';
            case 'updated':           return 'UPDATED';
            case 'deleted':           return 'DELETED';
            case 'priority_added':    return 'CREATED';
            case 'priority_updated':  return 'UPDATED';
            case 'zone_added':        return 'CREATED';
            case 'zone_updated':      return 'UPDATED';
            case 'provider_removed':  return 'DELETED';
            default:            return (event ?? 'UNKNOWN').replace(/_/g, ' ').toUpperCase();
        }
    }

    protected changedFields(entry: Activity): ChangedField[] {
        const attributes = entry.properties?.attributes;
        if (!attributes || typeof attributes !== 'object') {
            return [];
        }
        const [first] = Object.entries(attributes);
        if (!first) {
            return [];
        }
        const [key, value] = first;
        return [{
            key,
            label: key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()),
            newValue: this.formatPropertyValue(value),
        }];
    }

    private formatPropertyValue(value: unknown): string {
        if (value == null) {
            return '—';
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        return String(value);
    }

    protected extraFieldCount(entry: Activity): number {
        const attributes = entry.properties?.attributes;
        if (!attributes || typeof attributes !== 'object') {
            return 0;
        }
        return Math.max(0, Object.keys(attributes).length - 1);
    }

    refresh(): void {
        this.isLoading.set(true);
        this._page$.next(this._page$.value);
    }

    protected goToPage(page: number): void {
        this.isLoading.set(true);
        this._page$.next(page);
        this._router.navigate([], {
            relativeTo: this._route,
            queryParams: { auditPage: page },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }
}
